import React, { useState } from 'react';
import {
    Grid,
    TextField,
    Button,
    Dialog,
    DialogContent,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import AppDialogTitle from '../../../ui/appDialogTitle';
import SectionTitle from '../../../ui/sectionTitle';
import { CatalogService } from '../../../../service/catalogService';

const validationSchema = Yup.object({
    name: Yup.string().required('Product name is required'),
    brandId: Yup.string().required('Brand is required'),
    modelNumber: Yup.string().required('Model number is required'),
    gender: Yup.string().oneOf(['men', 'women', 'unisex', ''], 'Invalid gender'),
    frameType: Yup.string().oneOf(['full-rim', 'semi-rimless', 'rimless', ''], 'Invalid frame type'),
    year: Yup.number().integer().min(1900).max(2100).nullable(),
    a: Yup.number().positive().nullable(),
    b: Yup.number().positive().nullable(),
    dbl: Yup.number().positive().nullable(),
    temple: Yup.number().positive().nullable(),
    ed: Yup.number().positive().nullable(),
});

export default function CatalogFrameEditDialog({ 
    open, 
    onClose, 
    frame, 
    onSuccess, 
    brands, 
    shapes, 
    materials 
}) {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const initialValues = {
        name: frame?.name || '',
        sku: frame?.sku || '',
        brandId: frame?.brandId || '',
        modelNumber: frame?.frame?.modelNumber || '',
        upcOrEan: frame?.frame?.upcOrEan || '',
        gender: frame?.frame?.gender || '',
        year: frame?.frame?.year || '',
        a: frame?.frame?.a || '',
        b: frame?.frame?.b || '',
        dbl: frame?.frame?.dbl || '',
        temple: frame?.frame?.temple || '',
        ed: frame?.frame?.ed || '',
        frameType: frame?.frame?.frameType || '',
        shapeId: frame?.frame?.shapeId || '',
        materialId: frame?.frame?.materialId || '',
        colorName: frame?.frame?.colorName || '',
        colorCode: frame?.frame?.colorCode || '',
        sizeCode: frame?.frame?.sizeCode || '',
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values, { resetForm }) => {
            setIsSubmitting(true);
            try {
                const formattedValues = {
                    ...values,
                    year: values.year ? parseInt(values.year, 10) : null,
                    a: values.a ? parseFloat(values.a) : null,
                    b: values.b ? parseFloat(values.b) : null,
                    dbl: values.dbl ? parseFloat(values.dbl) : null,
                    temple: values.temple ? parseFloat(values.temple) : null,
                    ed: values.ed ? parseFloat(values.ed) : null,
                    shapeId: values.shapeId || null,
                    materialId: values.materialId || null,
                    upcOrEan: values.upcOrEan || null,
                    sku: values.sku || null,
                    colorName: values.colorName || null,
                    colorCode: values.colorCode || null,
                    sizeCode: values.sizeCode || null,
                    gender: values.gender || null,
                    frameType: values.frameType || null,
                };

                const response = await CatalogService.updateFrame(frame.id, formattedValues);
                const { code, data, message } = response;

                if (code === 0) {
                    toast.success(message || 'Frame updated successfully');
                    resetForm();
                    onSuccess(data);
                    onClose();
                } else {
                    toast.error(message || 'Failed to update frame');
                }
            } catch (error) {
                console.error(error);
                toast.error(error?.response?.data?.message || error.message || 'Failed to update frame');
            } finally {
                setIsSubmitting(false);
            }
        },
    });

    const { values, errors, touched, handleChange, handleSubmit, resetForm } = formik;

    const handleClose = () => {
        resetForm();
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <AppDialogTitle title="Edit Catalog Frame" onClose={handleClose} />

            <DialogContent>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        {/* Basic Information */}
                        <Grid item xs={12}>
                            <SectionTitle title="Basic Information" />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                name="name"
                                label="Product Name"
                                value={values.name}
                                onChange={handleChange}
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                fullWidth
                                required
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                name="sku"
                                label="SKU"
                                value={values.sku}
                                onChange={handleChange}
                                error={touched.sku && Boolean(errors.sku)}
                                helperText={touched.sku && errors.sku}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" required error={touched.brandId && Boolean(errors.brandId)}>
                                <InputLabel>Brand</InputLabel>
                                <Select
                                    name="brandId"
                                    value={values.brandId}
                                    onChange={handleChange}
                                    label="Brand"
                                >
                                    {brands?.map((brand) => (
                                        <MenuItem key={brand.id} value={brand.id}>
                                            {brand.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {touched.brandId && errors.brandId && <span style={{ color: 'red', fontSize: '0.75rem' }}>{errors.brandId}</span>}
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                name="modelNumber"
                                label="Model Number"
                                value={values.modelNumber}
                                onChange={handleChange}
                                error={touched.modelNumber && Boolean(errors.modelNumber)}
                                helperText={touched.modelNumber && errors.modelNumber}
                                fullWidth
                                required
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                name="upcOrEan"
                                label="UPC/EAN"
                                value={values.upcOrEan}
                                onChange={handleChange}
                                error={touched.upcOrEan && Boolean(errors.upcOrEan)}
                                helperText={touched.upcOrEan && errors.upcOrEan}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        {/* Frame Details */}
                        <Grid item xs={12}>
                            <SectionTitle title="Frame Details" />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Gender</InputLabel>
                                <Select
                                    name="gender"
                                    value={values.gender}
                                    onChange={handleChange}
                                    label="Gender"
                                >
                                    <MenuItem value="">None</MenuItem>
                                    <MenuItem value="men">Men</MenuItem>
                                    <MenuItem value="women">Women</MenuItem>
                                    <MenuItem value="unisex">Unisex</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                name="year"
                                label="Year"
                                type="number"
                                value={values.year}
                                onChange={handleChange}
                                error={touched.year && Boolean(errors.year)}
                                helperText={touched.year && errors.year}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Frame Type</InputLabel>
                                <Select
                                    name="frameType"
                                    value={values.frameType}
                                    onChange={handleChange}
                                    label="Frame Type"
                                >
                                    <MenuItem value="">None</MenuItem>
                                    <MenuItem value="full-rim">Full Rim</MenuItem>
                                    <MenuItem value="semi-rimless">Semi Rimless</MenuItem>
                                    <MenuItem value="rimless">Rimless</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Shape</InputLabel>
                                <Select
                                    name="shapeId"
                                    value={values.shapeId}
                                    onChange={handleChange}
                                    label="Shape"
                                >
                                    <MenuItem value="">None</MenuItem>
                                    {shapes?.map((shape) => (
                                        <MenuItem key={shape.id} value={shape.id}>
                                            {shape.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Material</InputLabel>
                                <Select
                                    name="materialId"
                                    value={values.materialId}
                                    onChange={handleChange}
                                    label="Material"
                                >
                                    <MenuItem value="">None</MenuItem>
                                    {materials?.map((material) => (
                                        <MenuItem key={material.id} value={material.id}>
                                            {material.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Measurements */}
                        <Grid item xs={12}>
                            <SectionTitle title="Measurements (mm)" />
                        </Grid>

                        <Grid item xs={12} sm={2.4}>
                            <TextField
                                name="a"
                                label="A (Eye Size)"
                                type="number"
                                value={values.a}
                                onChange={handleChange}
                                error={touched.a && Boolean(errors.a)}
                                helperText={touched.a && errors.a}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={2.4}>
                            <TextField
                                name="b"
                                label="B (Bridge)"
                                type="number"
                                value={values.b}
                                onChange={handleChange}
                                error={touched.b && Boolean(errors.b)}
                                helperText={touched.b && errors.b}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={2.4}>
                            <TextField
                                name="dbl"
                                label="DBL"
                                type="number"
                                value={values.dbl}
                                onChange={handleChange}
                                error={touched.dbl && Boolean(errors.dbl)}
                                helperText={touched.dbl && errors.dbl}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={2.4}>
                            <TextField
                                name="temple"
                                label="Temple"
                                type="number"
                                value={values.temple}
                                onChange={handleChange}
                                error={touched.temple && Boolean(errors.temple)}
                                helperText={touched.temple && errors.temple}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={2.4}>
                            <TextField
                                name="ed"
                                label="ED"
                                type="number"
                                value={values.ed}
                                onChange={handleChange}
                                error={touched.ed && Boolean(errors.ed)}
                                helperText={touched.ed && errors.ed}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        {/* Color & Size */}
                        <Grid item xs={12}>
                            <SectionTitle title="Color & Size" />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                name="colorName"
                                label="Color Name"
                                value={values.colorName}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                name="colorCode"
                                label="Color Code"
                                value={values.colorCode}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                name="sizeCode"
                                label="Size Code"
                                value={values.sizeCode}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                placeholder="e.g., 52-18-140"
                            />
                        </Grid>

                        {/* Submit Button */}
                        <Grid item xs={12}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
        </Dialog>
    );
}
